import { useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { Skeleton } from '../../../../components/Skeleton';
import { Stepper } from '../../../../components/Stepper';
import { useToast } from '../../../../components/toastContext';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { sampleDevelopmentNeeds, samplePlanDocument } from '../utils/sampleDevelopmentPlan';
import { focusValidationError } from '../../../../lib/validationFocus';
import { competenciesApi } from '../../../human-resources/competencies/competencyApi';
import {
  COMPETENCY_LEVEL_LABELS,
  type CompetencyLevel,
  type CompetencyOption,
} from '../../../human-resources/personnel/personnelTypes';
import { surveyRespondentsApi } from '../surveyApi';
import type { SurveyDevelopmentNeed, SurveyResponseView } from '../surveyTypes';
import { SURVEY_PLAN_STATUS, isPlanLocked } from '../surveyTypes';
import { isBlankNeed, needProblem, type DraftNeed } from '../utils/developmentNeeds';
import { DevelopmentNeedsGrid } from './DevelopmentNeedsGrid';
import { ResponseAnswers } from './SurveyResponseViewModal';

const ID = 'survey-development-plan';

const STEPS = [
  { key: 'preview', label: 'Response Preview' },
  { key: 'needs', label: 'Identified Development Needs' },
  { key: 'plan', label: 'Development Plan' },
] as const;

export type SurveyDevelopmentPlanStep = (typeof STEPS)[number]['key'];

type StepKey = SurveyDevelopmentPlanStep;

function toDraft(need: SurveyDevelopmentNeed): DraftNeed {
  return {
    key: need.id,
    id: need.id,
    competencyId: need.competencyId,
    currentLevel: String(need.currentLevel),
    targetLevel: String(need.targetLevel),
    gap: need.gap ?? '',
  };
}

/**
 * An assessed employee's development plan — what clicking a name on Survey › Development Plan
 * opens. A STEPPER, through the shared `<Stepper>`:
 *
 * 1. **Response Preview** — their answers and the assessment, read only (`<ResponseAnswers>`, the
 *    same view the answers window shows).
 * 2. **Identified Development Needs** — one row per competency: the level held, the level
 *    targeted, and the gap in the assessor's words. The 201 file's own level ladder.
 * 3. **Development Plan** — a rich-text document.
 *
 * Steps 2 and 3 save together on the last step's Submit, with the confirm tick there only.
 * Writing takes `surveys.plan`; without it the steps show what is recorded, read only.
 *
 * **No Autogenerate**, and that is the rule rather than an omission: the plan is an UNCONTROLLED
 * rich-text document a generator cannot write honestly, and the needs are an assessor's judgement.
 */
export function SurveyDevelopmentPlanModal({
  open,
  surveyId,
  respondentId,
  respondentName,
  onClose,
  onSaved,
  initialStep = 'preview',
}: {
  open: boolean;
  surveyId: string;
  respondentId: string;
  respondentName: string;
  onClose: () => void;
  onSaved: () => void;
  /**
   * The step the form OPENS on — for a caller editing one section of the plan, so the reader lands
   * where that section is. Every step stays reachable, and the save is still the whole plan.
   */
  initialStep?: SurveyDevelopmentPlanStep;
}) {
  const { hasPermission } = useAuth();
  const toast = useToast();

  const [view, setView] = useState<SurveyResponseView | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [competencies, setCompetencies] = useState<CompetencyOption[]>([]);

  const [step, setStep] = useState(() => Math.max(0, STEPS.findIndex((s) => s.key === initialStep)));
  const [needs, setNeeds] = useState<DraftNeed[]>([]);
  const [plan, setPlan] = useState('');
  // Bumped by Autogenerate: the editor takes its value ONCE, so a sample shows only in a fresh editor.
  const [editorKey, setEditorKey] = useState(0);
  // Whether approval sends the plan to the employee on its own — the plan step's tick.
  const [autoSend, setAutoSend] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    surveyRespondentsApi
      .get(surveyId, respondentId, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;

        setView(next);
        setNeeds(next.developmentNeeds.map(toDraft));
        setPlan(next.developmentPlan ?? '');
        setAutoSend(next.developmentPlanAutoSend === true);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setLoadError(caught instanceof ApiError ? caught : new ApiError('Could not load this development plan.', 0));
      });

    // The framework, for the needs' picker — through `surveys.plan`'s implied read. A reader
    // without it gets an empty list, which the read-only steps never need.
    competenciesApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setCompetencies(next);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [surveyId, respondentId]);

  // A plan an approver holds, or has approved, opens read only — the server refuses the save anyway.
  const locked = view !== null && isPlanLocked(view.developmentPlanStatus);
  const canWrite = hasPermission('surveys.plan') && view !== null && view.isActive && !locked;
  const current: StepKey = STEPS[step].key;
  const isLastStep = step === STEPS.length - 1;

  const needsValid = needs.every((n) => needProblem(n) === null);
  const canSave = canWrite && needsValid && isLastStep && confirmed && !saving;

  // A recorded competency the framework has since retired is still offered on its own row.
  const competencyOptions = (selected: string) => [
    ...competencies.map((c) => ({ value: c.id, label: c.name, sublabel: c.category ?? undefined })),
    ...(selected && !competencies.some((c) => c.id === selected)
      ? [{ value: selected, label: view?.developmentNeeds.find((n) => n.competencyId === selected)?.competencyName ?? 'Retired competency' }]
      : []),
  ];

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const saved = await surveyRespondentsApi.saveDevelopmentPlan(surveyId, respondentId, {
        needs: needs
          .filter((n) => !isBlankNeed(n))
          .map((n) => ({
            id: n.id,
            competencyId: n.competencyId || null,
            currentLevel: n.currentLevel === '' ? null : (Number(n.currentLevel) as CompetencyLevel),
            targetLevel: n.targetLevel === '' ? null : (Number(n.targetLevel) as CompetencyLevel),
            gap: n.gap.trim() || null,
          })),
        plan: plan.trim() ? plan : null,
        autoSend,
      });

      setView(saved);
      toast.success(`Saved ${respondentName}'s development plan.`);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('The development plan could not be saved.', 0);

      setError(failure);

      // A refusal names a need's row; the Needs step holds it, so go there before focusing.
      const fieldIds: Record<string, string> = {};
      const kept = needs.map((n, i) => ({ n, i })).filter(({ n }) => !isBlankNeed(n));

      kept.forEach(({ i }, sentIndex) => {
        for (const [field, cell] of [
          ['CompetencyId', 'competencyId'],
          ['CurrentLevel', 'currentLevel'],
          ['TargetLevel', 'targetLevel'],
          ['Gap', 'gap'],
        ] as const) {
          fieldIds[`Needs[${sentIndex}].${field}`] = `${ID}-needs.${i}.${cell}`;
          fieldIds[`needs[${sentIndex}].${field[0].toLowerCase()}${field.slice(1)}`] = `${ID}-needs.${i}.${cell}`;
        }
      });

      if (Object.keys(failure.fields ?? {}).some((k) => k.toLowerCase().startsWith('needs'))) {
        setStep(1);
        window.requestAnimationFrame(() =>
          window.requestAnimationFrame(() => focusValidationError(failure, { idPrefix: ID, fieldIds })),
        );
      } else {
        focusValidationError(failure, { idPrefix: ID, fieldIds });
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Development plan — ${respondentName}`}
      // Sized to what it holds (product call, 09 Oct 2026): the Needs grid at 760px of columns plus
      // its two 44px gutters and the body's padding is what `2xl` (896px) fits — no wider.
      size="2xl"
      id={`${ID}-modal`}
      /*
        Autogenerate fills the Identified Development Needs from the live competencies. It leaves
        the Development Plan document alone: the editor is UNCONTROLLED after mount, so a sample
        pushed into it would not show — the same call the Positions form makes (frontend-forms).
      */
      autogenerate={
        canWrite
          ? {
              onGenerate: () => {
                setNeeds(sampleDevelopmentNeeds(competencies.map((c) => c.id)).map((n) => ({ key: uid(), id: null, ...n })));
                // The plan document too (product call, 13 Oct 2026) — in a fresh editor, since it takes its value once.
                setPlan(samplePlanDocument());
                setEditorKey((k) => k + 1);
              },
              disabled: saving || competencies.length === 0,
            }
          : undefined
      }
      // The tick on the LAST step only, and only where there is something to write.
      confirm={
        canWrite && isLastStep
          ? { id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }
          : undefined
      }
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            {canWrite ? 'Cancel' : 'Close'}
          </Button>

          {/* Back from the second step on; never gated — going back is how a step gets fixed. */}
          {step > 0 ? (
            <Button id={`${ID}-back`} variant="secondary" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={saving}>
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            <Button
              id={`${ID}-next`}
              variant="secondary"
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              disabled={view === null || (current === 'needs' && canWrite && !needsValid)}
            >
              Next
            </Button>
          ) : null}

          {canWrite ? (
            <Button
              id={`${ID}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Stepper
          id={ID}
          steps={STEPS.map(({ key, label }) => ({ key, label }))}
          activeIndex={step}
          onJump={(index) => {
            // Back is free; forward only past a Needs step that holds.
            if (index <= step || !canWrite || needsValid) setStep(index);
          }}
        />

        {locked ? (
          <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            {view?.developmentPlanStatus === SURVEY_PLAN_STATUS.Accepted
              ? 'The employee accepted this development plan, so it is final.'
              : view?.developmentPlanStatus === SURVEY_PLAN_STATUS.Approved
                ? 'This development plan has been approved, so it can no longer be changed.'
                : 'This development plan is waiting for approval, so it cannot be changed. Retract it from Approvals first.'}
          </p>
        ) : null}

        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {loadError ? (
          <ErrorState message={loadError.message} correlationId={loadError.correlationId} />
        ) : !view ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : current === 'preview' ? (
          <div className="flex flex-col gap-4">
            <ResponseAnswers view={view} respondentName={respondentName} />
          </div>
        ) : current === 'needs' ? (
          canWrite ? (
            <DevelopmentNeedsGrid
              idPrefix={ID}
              needs={needs}
              onChange={setNeeds}
              competencyOptions={competencyOptions}
              disabled={saving}
            />
          ) : (
            <PreviewGrid<SurveyDevelopmentNeed>
              rows={view.developmentNeeds}
              rowKey={(n) => n.id}
              empty="No development needs identified yet."
              columns={[
                { header: 'Competency', width: 220, render: (n) => <TextCell value={n.competencyName} /> },
                { header: 'Current level', width: 140, render: (n) => <TextCell value={COMPETENCY_LEVEL_LABELS[n.currentLevel]} /> },
                { header: 'Target level', width: 140, render: (n) => <TextCell value={COMPETENCY_LEVEL_LABELS[n.targetLevel]} /> },
                { header: 'Gap identified', width: 260, render: (n) => <TextCell value={n.gap ?? null} /> },
              ]}
            />
          )
        ) : canWrite ? (
          <div className="flex flex-col gap-4">
            <LazyRichTextField
              key={editorKey}
              id={`${ID}-plan`}
              label="Development Plan"
              value={plan}
              onChange={setPlan}
              maxLength={8000}
              disabled={saving}
            />

            {/*
              APPROVED IS NOT SENT. Ticked, the approval itself sends the plan to the employee;
              unticked, it stays with Learning & Development until somebody sends it from the
              plan's banner. Either way the employee sees nothing before it is approved.
            */}
            <div className="flex flex-col gap-1">
              <label htmlFor={`${ID}-auto-send`} className="flex items-center gap-2.5">
                <Checkbox
                  id={`${ID}-auto-send`}
                  checked={autoSend}
                  onChange={(e) => setAutoSend(e.target.checked)}
                  disabled={saving}
                />
                <span className="text-sm text-slate-700">Send to the employee automatically when approved</span>
              </label>
              <p className="pl-[26px] text-xs text-slate-500">
                {autoSend
                  ? 'The employee sees the plan as soon as it is approved, and nothing before that.'
                  : 'Once approved, the plan waits to be sent from its banner. The employee sees nothing until then.'}
              </p>
            </div>
          </div>
        ) : (
          // The one renderer of stored markup — safe only because the server sanitised it on write.
          <RichTextDocument html={view.developmentPlan ?? null} />
        )}
      </div>
    </Modal>
  );
}
